/**
 * The last screen before ten thousand messages go out (PLAN §12.4, §33).
 *
 * It exists to make one number impossible to miss: **what this campaign
 * costs.** Not the recipient count — the SMS count, which is a different and
 * larger number whenever a message runs past one segment, and which is the
 * number the invoice is calculated from.
 *
 * Two guards, and they are deliberately different in kind. The **checkbox** is
 * a claim that somebody read the figures; it is unticked on arrival and
 * unticked again after a refusal, because a checkbox that remembers being
 * ticked is a checkbox that gets ticked once and never read again. The
 * **`where status = 'draft'`** in `start_sending` is what actually prevents a
 * second campaign, and it holds whether or not anybody ticked anything.
 *
 * The draft is read back from the database rather than carried across from the
 * previous screen. It survives a refresh that way, and — more to the point —
 * what is confirmed is what was *written*, not what the browser believed it
 * wrote.
 */

import {
  campaign,
  campaignPhones,
  distribution,
  gate,
  lookup,
  recentContacts,
  type CampaignRow,
  type Distribution,
  type LookupResult,
} from '../data.ts';
import { el, moment, number, replace, type Child } from '../dom.ts';
import { fatigueView, FATIGUE_DAYS } from '../../fatigue-view.ts';
import { lookupView } from '../../lookup-view.ts';
import { translate as t, translateKey } from '../i18n.ts';
import { failure, link, loading, navigate, renderShell } from '../shell.ts';
import { forgetHealth } from './health.ts';

/**
 * How long Send waits before it becomes irreversible (TASKS §C14.7).
 *
 * **The campaign is not started during this window — it is still a draft**, so
 * "stop" is not a cancellation racing the pipeline, it is simply not having
 * begun. That distinction is the whole feature: `cancel_sending` stops a
 * campaign at the next batch boundary, which on a five-second clock means the
 * first twenty messages have already gone. Nothing here has gone.
 *
 * Closing the tab during the countdown sends nothing and leaves the draft where
 * it was, which is the safe way for this to fail.
 */
const UNDO_SECONDS = 15;

export function renderConfirm(root: Element, email: string, id: number): void {
  const body = el('section', { class: 'card' }, loading());
  renderShell(root, { active: 'campaign.new', email, title: t('confirm.title') }, body);

  const load = () => {
    void Promise.all([campaign(id), distribution(id)])
      .then(([row, spread]) => {
        if (row === undefined) {
          replace(body, el('h1', {}, t('error.notFound')));
          return;
        }
        if (row.status !== 'draft') {
          // Already started, by another tab or by a refreshed POST. Saying so
          // and pointing at the status screen is more useful than a refusal.
          replace(
            body,
            el('h1', {}, t('confirm.title')),
            el('div', { class: 'finding error' }, el('span', {}, t('confirm.notDraft'))),
            el('a', { class: 'button-link', href: `/campaign/${id}`, onclick: link(`/campaign/${id}`) }, t('history.open')),
          );
          return;
        }
        replace(body, ...confirmation(row, spread));
      })
      .catch(() => replace(body, failure(load)));
  };

  function confirmation(row: CampaignRow, spread: readonly Distribution[]): readonly Child[] {
    const parts = totalParts(spread, row.estimated_parts);
    const problem = el('div', { class: 'finding error', hidden: true });

    const acknowledge = el('input', { type: 'checkbox', id: 'acknowledge' });
    const send = el('button', { type: 'submit' }, t('confirm.send'));

    const form = el(
      'form',
      { class: 'confirm' },
      el(
        'label',
        { class: 'choice', for: 'acknowledge' },
        acknowledge,
        // The figures are repeated *inside* the acknowledgement, not merely
        // above it. A tick beside "I understand" is a tick beside nothing.
        t('confirm.acknowledge', { recipients: row.recipient_count, parts }),
      ),
      problem,
      el(
        'div',
        { class: 'actions' },
        send,
        el('a', { class: 'button-link secondary', href: '/campaign/new', onclick: link('/campaign/new') }, t('confirm.back')),
      ),
    );

    form.addEventListener('submit', (event) => {
      event.preventDefault();

      if (!acknowledge.checked) {
        problem.textContent = t('confirm.notAcknowledged');
        problem.removeAttribute('hidden');
        return;
      }

      // A last, deliberate stop. The figures are in the question, so the answer
      // is being given to a specific campaign rather than to a habit.
      if (!window.confirm(`${t('confirm.modal.title')}\n\n${t('confirm.modal.body', { recipients: row.recipient_count, parts })}`)) {
        return;
      }

      send.disabled = true;
      countdown(form, () => {
        void gate({ action: 'send', campaignId: id })
          .then(() => {
            // The strip's cached answer is about to be out of date in the one
            // way that matters: there is now a campaign in flight.
            forgetHealth();
            navigate(`/campaign/${id}`);
          })
          .catch(() => {
            problem.textContent = t('app.loadFailed');
            problem.removeAttribute('hidden');
            send.disabled = false;
            // Unticked again, so the next press is a second decision rather
            // than the same one leaking through.
            acknowledge.checked = false;
          });
      }, () => {
        send.disabled = false;
        acknowledge.checked = false;
      });
    });

    const fatigue = el('div', { class: 'fatigue' });
    const numbers = el('section', { class: 'card lookup' }, el('h2', {}, t('lookup.title')));

    // Both need the list itself, and both are allowed to fail quietly: neither
    // is a reason to stand between somebody and a campaign they have already
    // decided to send.
    void campaignPhones(id)
      .then((list) => {
        showFatigue(fatigue, list.phones);
        showLookup(numbers, list.phones);
      })
      .catch(() => undefined);

    return [
      el('h1', {}, t('confirm.title')),
      el('p', { class: 'muted' }, t('confirm.intro')),
      fatigue,
      el(
        'div',
        { class: 'figures' },
        el('p', { class: 'figure' }, el('output', {}, number(row.recipient_count)), el('span', {}, t('confirm.recipients'))),
        el('p', { class: 'figure' }, el('output', {}, number(parts)), el('span', {}, t('confirm.parts'))),
      ),
      el('h2', {}, t('confirm.message')),
      el('pre', { class: 'message-preview' }, row.template),
      spread.length > 1 ? distributionTable(spread) : false,
      form,
      numbers,
    ];
  }

  load();
}

/**
 * Only shown when there is more than one row, because that is the only time it
 * says anything: a campaign where every message is one SMS is already fully
 * described by the total above.
 */
function distributionTable(spread: readonly Distribution[]): Child {
  const recipients = spread.reduce((total, row) => total + Number(row.recipients), 0);

  return el(
    'section',
    { class: 'distribution' },
    el('h2', {}, t('confirm.distribution.title')),
    el('p', { class: 'muted' }, t('confirm.distribution.affected', { recipients })),
    el(
      'table',
      {},
      el(
        'thead',
        {},
        el(
          'tr',
          {},
          el('th', { class: 'numeric' }, t('confirm.distribution.parts')),
          el('th', { class: 'numeric' }, t('confirm.distribution.recipients')),
          el('th', { class: 'numeric' }, t('confirm.distribution.subtotal')),
        ),
      ),
      el(
        'tbody',
        {},
        ...spread.map((row) =>
          el(
            'tr',
            {},
            el('td', { class: 'numeric' }, number(row.parts)),
            el('td', { class: 'numeric' }, number(Number(row.recipients))),
            el('td', { class: 'numeric' }, number(row.parts * Number(row.recipients))),
          ),
        ),
      ),
    ),
  );
}

/**
 * The sum of the distribution, and the stored estimate only as a fallback.
 *
 * They should agree — the estimate was written from the same rows — but if they
 * ever do not, the one derived from the rows in front of you is the one to show.
 */
function totalParts(spread: readonly Distribution[], estimate: number): number {
  if (spread.length === 0) return estimate;
  return spread.reduce((total, row) => total + row.parts * Number(row.recipients), 0);
}

// ---------------------------------------------------------------------------
// The undo window

/**
 * Fifteen seconds between pressing Send and anything happening.
 *
 * **Nothing is started and then stopped.** The campaign stays a draft for the
 * whole countdown, so "stop" is not a cancellation racing a five-second clock —
 * it is simply never having begun. `cancel_sending` exists for the other case
 * and cannot make this promise: by the time it lands, the first batch is with
 * the provider.
 *
 * Fifteen because the mistake this catches is recognised immediately — the
 * wrong list, the wrong month in the text — and a longer wait would train
 * people to walk away from the screen, which is the one thing that makes it
 * useless.
 */
function countdown(form: Element, go: () => void, stop: () => void): void {
  let left = UNDO_SECONDS;
  let timer = 0;

  const label = el('strong', {}, t('undo.title', { seconds: left }));
  const panel = el(
    'div',
    { class: 'finding undo', role: 'alert' },
    label,
    el('span', {}, t('undo.body')),
    el(
      'div',
      { class: 'actions' },
      el('button', { type: 'button', class: 'danger', onclick: () => finish(false) }, t('undo.cancel')),
      el('button', { type: 'button', class: 'secondary', onclick: () => finish(true) }, t('undo.now')),
    ),
  );

  const finish = (send: boolean) => {
    window.clearInterval(timer);
    panel.remove();
    if (send) go();
    else {
      stop();
      // Said out loud. A countdown that simply vanishes leaves somebody
      // wondering whether they stopped it in time.
      form.appendChild(el('div', { class: 'finding', role: 'status' }, el('span', {}, t('undo.cancelled'))));
    }
  };

  timer = window.setInterval(() => {
    left -= 1;
    if (left <= 0) finish(true);
    else label.textContent = t('undo.title', { seconds: left });
  }, 1000);

  form.appendChild(panel);
}

// ---------------------------------------------------------------------------
// Who heard from us recently

/**
 * A warning, never a block.
 *
 * There are good reasons to message somebody twice in a fortnight — a
 * correction, a second branch, a genuinely different offer — and a panel that
 * refused would be worked around by the end of the week. What it can do is put
 * the fact where nobody can miss it, on the screen where the decision is made.
 */
function showFatigue(panel: Element, phones: readonly string[]): void {
  if (phones.length === 0) return;

  void recentContacts(phones, FATIGUE_DAYS)
    .then((contacts) => {
      const view = fatigueView(contacts);
      if (view.count === 0) return;

      replace(
        panel,
        el(
          'details',
          { class: 'finding warn' },
          el(
            'summary',
            {},
            el('strong', {}, t('fatigue.title')),
            ` ${t('fatigue.body', { count: number(view.count), days: view.days })}`,
          ),
          el(
            'div',
            { class: 'table-wrap' },
            el(
              'table',
              {},
              el(
                'thead',
                {},
                el(
                  'tr',
                  {},
                  el('th', {}, t('campaign.preview.phone')),
                  el('th', {}, t('fatigue.lastSent')),
                  el('th', {}, t('fatigue.campaign')),
                ),
              ),
              el(
                'tbody',
                {},
                // Capped: the point is that it happened and roughly how much,
                // and a thousand-row table inside a warning is a wall.
                ...view.rows.slice(0, 50).map((row) =>
                  el(
                    'tr',
                    {},
                    el('td', {}, row.phone),
                    el('td', {}, moment(row.at)),
                    el('td', {}, `#${row.campaignId}`),
                  ),
                ),
              ),
            ),
          ),
        ),
      );
    })
    .catch(() => undefined);
}

// ---------------------------------------------------------------------------
// Asking the operator about the numbers

/**
 * Free at the provider, so the only cost is time — which is why it is a button
 * rather than something that happens on load. A ten thousand row campaign is a
 * hundred requests, and starting those because somebody opened a screen would
 * be rude to the provider and slow for everybody else.
 *
 * **It is honest about what it proves.** `/api/def` answers which operator a
 * number belongs to, not whether anybody is holding it: an unassigned number in
 * a real range comes back recognised. So this catches typos, truncated cells
 * and foreign numbers, and it does not catch a number disconnected last week.
 */
function showLookup(panel: Element, phones: readonly string[]): void {
  if (phones.length === 0) return;

  const results = el('div', {});
  const button = el('button', { type: 'button', class: 'secondary' }, t('lookup.check', { count: number(phones.length) }));

  button.addEventListener('click', () => {
    button.disabled = true;
    const found: LookupResult[] = [];

    const step = (from: number): Promise<void> => {
      if (from >= phones.length) return Promise.resolve();

      replace(results, el('p', { class: 'muted' }, t('lookup.checking', { done: number(from), total: number(phones.length) })));

      return lookup(phones.slice(from, from + LOOKUP_BATCH)).then((answer) => {
        found.push(...answer.results);
        // The provider stopped answering part-way. What came back is real and
        // is shown; pretending the rest was fine is the failure mode this
        // whole feature exists to prevent.
        return answer.complete ? step(from + LOOKUP_BATCH) : undefined;
      });
    };

    void step(0)
      .then(() => replace(results, ...lookupSummary(found)))
      .catch(() => replace(results, el('div', { class: 'finding error' }, el('span', {}, t('app.loadFailed')))))
      .finally(() => {
        button.disabled = false;
      });
  });

  panel.appendChild(el('p', { class: 'muted' }, t('lookup.free')));
  panel.appendChild(el('div', { class: 'actions' }, button));
  panel.appendChild(results);
}

/** `campaign`'s own per-call limit, so a batch is never refused for being one over. */
const LOOKUP_BATCH = 100;

function lookupSummary(answers: readonly LookupResult[]): readonly Child[] {
  const view = lookupView(answers);
  const bad = view.unknown + view.malformed;

  return [
    el(
      'div',
      { class: 'figures' },
      el('p', { class: 'figure' }, el('output', {}, number(view.known)), el('span', {}, t('lookup.known'))),
      el('p', { class: 'figure' }, el('output', {}, number(view.unknown)), el('span', {}, t('lookup.notInDatabase'))),
      el('p', { class: 'figure' }, el('output', {}, number(view.malformed)), el('span', {}, t('lookup.badPhone'))),
      el('p', { class: 'figure' }, el('output', {}, number(view.failed)), el('span', {}, t('lookup.unavailable'))),
    ),
    view.operators.length === 0
      ? false
      : el(
          'p',
          { class: 'muted' },
          `${t('lookup.operator')}: ${view.operators.map((one) => `${one.name} — ${number(one.count)}`).join(', ')}`,
        ),
    bad === 0
      ? el('div', { class: 'finding' }, el('span', {}, t('lookup.allKnown')))
      : el(
          'details',
          { class: 'finding warn' },
          el('summary', {}, el('strong', {}, t('lookup.unknownWarning'))),
          el(
            'div',
            { class: 'table-wrap' },
            el(
              'table',
              {},
              el('thead', {}, el('tr', {}, el('th', {}, t('campaign.preview.phone')), el('th', {}, t('lookup.result')))),
              el(
                'tbody',
                {},
                ...view.problems.slice(0, 200).map((problem) =>
                  el('tr', {}, el('td', {}, problem.phone), el('td', {}, translateKey(problem.messageKey))),
                ),
              ),
            ),
          ),
        ),
  ];
}
